import { forwardRef } from 'react';
import classnames from 'classnames';

import { useDisabled, DisabledContext } from '@hh.ru/magritte-common-use-disabled';
import type { PolymorphicForwardRefRenderFunc, PolymorphicComponentWithRef } from '@hh.ru/magritte-types';
import type {
    CheckableCardBorderRadius,
    CheckableCardElementProps,
    CheckableCardPadding,
} from '@hh.ru/magritte-ui-checkable-card/types';

import styles from './checkable-card.less';

const genBorderRadiusClasses = (radiusProps: {
    TopLeft?: CheckableCardBorderRadius;
    TopRight?: CheckableCardBorderRadius;
    BottomLeft?: CheckableCardBorderRadius;
    BottomRight?: CheckableCardBorderRadius;
}) =>
    Object.entries(radiusProps).reduce<string[]>(
        (acc, [key, value]) =>
            value !== undefined ? [...acc, styles[`border${key as keyof typeof radiusProps}Radius${value}`]] : acc,
        []
    );

const genPaddingProps = (paddingProps: {
    Left?: CheckableCardPadding;
    Right?: CheckableCardPadding;
    Top?: CheckableCardPadding;
    Bottom?: CheckableCardPadding;
}) =>
    Object.entries(paddingProps).reduce<Record<string, number>>(
        (acc, [key, value]) => (value !== undefined ? { ...acc, [`padding${key}`]: value } : acc),
        {}
    );

const CheckableCardElementForwardRefRenderFunc: PolymorphicForwardRefRenderFunc<CheckableCardElementProps, 'div'> = (
    {
        children,
        padding,
        paddingLeft,
        paddingRight,
        paddingTop,
        paddingBottom,
        borderRadius,
        borderTopLeftRadius,
        borderTopRightRadius,
        borderBottomLeftRadius,
        borderBottomRightRadius,
        toneHover,
        disabled,
        flexible,
        Element = 'div',
        input,
        contentRef,
        checked = false,
        focusVisible = false,
        ...props
    },
    ref
) => {
    const paddingProps = genPaddingProps({
        Left: paddingLeft,
        Right: paddingRight,
        Top: paddingTop,
        Bottom: paddingBottom,
    });

    const forceDisabled = useDisabled();

    return (
        <Element
            {...props}
            className={classnames(
                styles.card,
                styles[`borderRadius${borderRadius}`],
                genBorderRadiusClasses({
                    TopLeft: borderTopLeftRadius,
                    TopRight: borderTopRightRadius,
                    BottomLeft: borderBottomLeftRadius,
                    BottomRight: borderBottomRightRadius,
                }),
                {
                    [styles.cardDisabled]: forceDisabled || !!disabled,
                    [styles.cardChecked]: checked,
                    [styles.cardHugContent]: !flexible,
                    [styles.cardToneHover]: toneHover,
                    'focus-visible': focusVisible,
                }
            )}
            data-interactive={true}
            ref={ref}
            tabIndex={input || forceDisabled || !!disabled ? -1 : 0}
        >
            {input}
            <div className={styles.content} ref={contentRef}>
                <div className={styles.contentBorderContainer} style={{ padding, ...paddingProps }}>
                    <div className={styles.contentOpacityContainer}>
                        <DisabledContext.Provider value={forceDisabled || !!disabled}>
                            {children}
                        </DisabledContext.Provider>
                    </div>
                </div>
            </div>
        </Element>
    );
};

export const CheckableCardElement: PolymorphicComponentWithRef<CheckableCardElementProps, 'div'> = forwardRef(
    CheckableCardElementForwardRefRenderFunc
);
